import React, { useEffect, useState } from "react";

import {
  useParams,
  useSearchParams,
} from "react-router-dom";

import {
  doc,
  getDoc,
} from "firebase/firestore";

import { db } from "../firebase";

import { useCart } from "../context/CartContext";


export default function ProductDetail() {

  const { id } = useParams();

  const [searchParams] = useSearchParams();

  const { addToCart } = useCart();


  /* =====================================================
     SOURCE
     
     Normal product:
     /product/P1

     Shop product:
     /product/ABC123?source=shop
  ===================================================== */

  const source = searchParams.get("source");


  /* =====================================================
     STATE
  ===================================================== */

  const [product, setProduct] = useState(null);

  const [variant, setVariant] = useState(null);

  const [loading, setLoading] = useState(true);


  /* =====================================================
     SAVE REFERRAL
  ===================================================== */

  useEffect(() => {

    const ref = searchParams.get("ref");

    if (!ref) return;

    const referral = {

      volunteerUid: ref,

      productId: id,

      referredAt: Date.now(),

      source: source || "product",

    };

    localStorage.setItem(
      "eyalReferral",
      JSON.stringify(referral)
    );

    console.log(
      "Referral saved:",
      referral
    );

  }, [id, searchParams, source]);


  /* =====================================================
     LOAD PRODUCT
  ===================================================== */

  useEffect(() => {

    const loadProduct = async () => {

      try {

        setLoading(true);

        setProduct(null);

        setVariant(null);


        /* =================================================
           SHOP PRODUCT
        ================================================= */

        if (source === "shop") {

          console.log(
            "Loading shop product:",
            id
          );

          const snap = await getDoc(
            doc(
              db,
              "shopproducts",
              id
            )
          );


          if (!snap.exists()) {

            console.error(
              "Shop product not found:",
              id
            );

            setLoading(false);

            return;

          }


          const data = snap.data();


          /*
           * shopproducts does not necessarily use
           * the same variants structure as products.
           *
           * Create a normalized variant so the
           * existing detail UI can still work.
           */

          const shopVariant = {

            variantId:
              data.variantId ||
              "default",

            name:
              data.name ||
              "Product",

            image:
              data.image ||
              "",

            price:
              Number(data.price || 0),

            mrp:
              Number(
                data.mrp ||
                data.price ||
                0
              ),

            discount:
              Number(
                data.discount || 0
              ),

            stock:
              Number(
                data.stock || 0
              ),

            unit:
              data.unit ||
              "",

            color:
              data.color ||
              "",

          };


          const normalizedProduct = {

            product_id: snap.id,

            source: "shop",

            ...data,

            variants: [shopVariant],

          };


          setProduct(
            normalizedProduct
          );

          setVariant(
            shopVariant
          );


          console.log(
            "Shop product loaded:",
            normalizedProduct
          );


          setLoading(false);

          return;

        }


        /* =================================================
           NORMAL Eyal MART PRODUCT
        ================================================= */

        console.log(
          "Loading normal product:",
          id
        );


        const snap = await getDoc(
          doc(
            db,
            "products",
            id
          )
        );


        if (!snap.exists()) {

          console.error(
            "Normal product not found:",
            id
          );

          setLoading(false);

          return;

        }


        const data = snap.data();


        setProduct({

          product_id: snap.id,

          source: "product",

          ...data,

        });


        setVariant(
          data.variants?.[0] || null
        );


        setLoading(false);


      } catch (error) {

        console.error(
          "Failed to load product:",
          error
        );

        setProduct(null);

        setVariant(null);

        setLoading(false);

      }

    };


    loadProduct();

  }, [id, source]);


  /* =====================================================
     LOADING
  ===================================================== */

  if (loading) {

    return (
      <div style={styles.center}>
        Loading…
      </div>
    );

  }


  /* =====================================================
     NOT FOUND
  ===================================================== */

  if (!product || !variant) {

    return (
      <div style={styles.center}>
        Not found
      </div>
    );

  }


  /* =====================================================
     PRODUCT DATA
  ===================================================== */

  const {

    brand,

    category,

    subCategory,

    description,

    variants = [],

    shopName,

  } = product;


  const {

    name,

    image,

    price,

    mrp,

    discount,

    stock,

    unit,

  } = variant;


  /* =====================================================
     ADD TO CART
  ===================================================== */

  const handleAddToCart = () => {

    /* =================================================
       SHOP PRODUCT
    ================================================= */

    if (source === "shop") {

      addToCart({

        type: "shop",

        id: product.product_id,

        shopId:
          product.shopId,

        ownerUid:
          product.ownerUid,

        shopName:
          product.shopName || "",

        name:
          product.name,

        brand:
          product.brand || "",

        image:
          product.image || "",

        price:
          Number(product.price || 0),

        mrp:
          Number(
            product.mrp ||
            product.price ||
            0
          ),

        stock:
          Number(product.stock || 0),

        unit:
          product.unit || "",

      });

      return;

    }


    /* =================================================
       NORMAL PRODUCT
    ================================================= */

    addToCart(
      product,
      variant
    );

  };


  /* =====================================================
     RENDER
  ===================================================== */

  return (

    <div style={styles.page}>


      {/* =================================================
         SHOP INFORMATION
      ================================================= */}

      {source === "shop" &&
        shopName && (

          <div style={styles.shopBox}>

            <div style={styles.shopIcon}>
              🏪
            </div>

            <div>

              <div style={styles.shopTitle}>
                {shopName}
              </div>

              <div style={styles.shopSubtitle}>
                Premium Shop
              </div>

            </div>

          </div>

        )}


      {/* =================================================
         MAIN IMAGE
      ================================================= */}

      <img

        src={image}

        alt={name}

        style={styles.mainImage}

      />


      {/* =================================================
         THUMBNAILS
      ================================================= */}

      {variants.length > 0 && (

        <div style={styles.thumbRow}>

          {variants.map((v, index) => (

            <img

              key={
                v.variantId ||
                index
              }

              src={v.image}

              alt=""

              onClick={() =>
                setVariant(v)
              }

              style={{

                ...styles.thumb,

                border:
                  (
                    v.variantId ===
                    variant.variantId
                  )

                    ? "2px solid #2563eb"

                    : "1px solid #e5e7eb",

              }}

            />

          ))}

        </div>

      )}


      {/* =================================================
         PRODUCT NAME
      ================================================= */}

      <h3 style={styles.title}>
        {name}
      </h3>


      {/* =================================================
         BRAND
      ================================================= */}

      {brand && (

        <p style={styles.brand}>
          {brand}
        </p>

      )}


      {/* =================================================
         SHOP NAME
      ================================================= */}

      {source === "shop" &&
        shopName && (

          <div style={styles.shopName}>
            🏪 {shopName}
          </div>

        )}


      {/* =================================================
         CATEGORY
      ================================================= */}

      {(category || subCategory) && (

        <div style={styles.meta}>

          {category}

          {subCategory &&
            ` • ${subCategory}`}

        </div>

      )}


      {/* =================================================
         DESCRIPTION
      ================================================= */}

      {description && (

        <p style={styles.description}>
          {description}
        </p>

      )}


      {/* =================================================
         COLOR DOTS
         
         Only useful when there are actual colors.
      ================================================= */}

      {variants.some(
        (v) => v.color
      ) && (

        <div style={styles.colorRow}>

          {variants.map(
            (v, index) => (

              <button

                key={
                  v.variantId ||
                  index
                }

                onClick={() =>
                  setVariant(v)
                }

                style={{

                  ...styles.colorDot,

                  background:
                    v.color ||
                    "#999",

                  outline:
                    v.variantId ===
                    variant.variantId

                      ? "2px solid #2563eb"

                      : "none",

                }}

              />

            )
          )}

        </div>

      )}


      {/* =================================================
         PRICE
      ================================================= */}

      <div style={styles.priceRow}>

        <span style={styles.price}>
          ₹{price}
        </span>


        {mrp > price && (

          <span style={styles.mrp}>
            ₹{mrp}
          </span>

        )}


        {discount > 0 && (

          <span style={styles.discount}>
            {discount}% OFF
          </span>

        )}

      </div>


      {/* =================================================
         STOCK
      ================================================= */}

      <div style={styles.stock}>

        {Number(stock) > 0

          ? `In Stock${
              unit
                ? ` • Unit : ${unit}`
                : ""
            }`

          : "Out of stock"}

      </div>


      {/* =================================================
         ADD TO CART
      ================================================= */}

      <button

        style={styles.btn}

        disabled={
          Number(stock) <= 0
        }

        onClick={
          handleAddToCart
        }

      >

        {Number(stock) <= 0
          ? "Out of Stock"
          : "Add to Cart"}

      </button>

    </div>

  );

}


/* =====================================================
   STYLES
===================================================== */

const styles = {

  page: {

    padding: 16,

    paddingTop: 70,

    paddingBottom: 90,

    background: "#fff",

    minHeight: "100vh",

    boxSizing: "border-box",

  },


  /* ---------------------------------
     SHOP BOX
  --------------------------------- */

  shopBox: {

    display: "flex",

    alignItems: "center",

    gap: 10,

    padding: 12,

    marginBottom: 12,

    background: "#f8fafc",

    borderRadius: 12,

    border: "1px solid #e5e7eb",

  },


  shopIcon: {

    width: 40,

    height: 40,

    borderRadius: 10,

    background: "#ede9fe",

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    fontSize: 20,

  },


  shopTitle: {

    fontSize: 15,

    fontWeight: 700,

    color: "#111827",

  },


  shopSubtitle: {

    fontSize: 12,

    color: "#7c3aed",

    fontWeight: 600,

    marginTop: 2,

  },


  shopName: {

    fontSize: 14,

    fontWeight: 600,

    color: "#374151",

    marginTop: 6,

  },


  /* ---------------------------------
     IMAGE
  --------------------------------- */

  mainImage: {

    width: "100%",

    height: 260,

    objectFit: "contain",

    background: "#fff",

  },


  /* ---------------------------------
     THUMBNAILS
  --------------------------------- */

  thumbRow: {

    display: "flex",

    gap: 8,

    margin: "10px 0",

    overflowX: "auto",

  },


  thumb: {

    width: 60,

    height: 60,

    objectFit: "contain",

    borderRadius: 8,

    background: "#fff",

    flexShrink: 0,

    cursor: "pointer",

  },


  /* ---------------------------------
     TEXT
  --------------------------------- */

  title: {

    fontSize: 20,

    margin: "12px 0 4px",

    color: "#111827",

  },


  brand: {

    color: "#6b7280",

    margin: "0 0 6px",

  },


  meta: {

    color: "#6b7280",

    fontSize: 13,

    marginBottom: 12,

  },


  description: {

    lineHeight: 1.5,

    color: "#374151",

  },


  /* ---------------------------------
     COLORS
  --------------------------------- */

  colorRow: {

    display: "flex",

    gap: 10,

    margin: "10px 0",

  },


  colorDot: {

    width: 26,

    height: 26,

    borderRadius: "50%",

    border: "1px solid #d1d5db",

    cursor: "pointer",

  },


  /* ---------------------------------
     PRICE
  --------------------------------- */

  priceRow: {

    display: "flex",

    gap: 8,

    alignItems: "center",

    marginTop: 14,

  },


  price: {

    fontSize: 22,

    fontWeight: 700,

    color: "#111827",

  },


  mrp: {

    textDecoration: "line-through",

    color: "#9ca3af",

    fontSize: 14,

  },


  discount: {

    color: "#16a34a",

    fontWeight: 600,

    fontSize: 13,

  },


  /* ---------------------------------
     STOCK
  --------------------------------- */

  stock: {

    marginTop: 10,

    fontSize: 14,

    color: "#374151",

  },


  /* ---------------------------------
     BUTTON
  --------------------------------- */

  btn: {

    width: "100%",

    height: 48,

    borderRadius: 12,

    border: "none",

    background: "#2563eb",

    color: "#fff",

    fontSize: 16,

    fontWeight: 600,

    marginTop: 14,

    cursor: "pointer",

  },


  /* ---------------------------------
     CENTER
  --------------------------------- */

  center: {

    paddingTop: 120,

    textAlign: "center",

    color: "#374151",

  },

};